// Core shared types for EngHustle
// Used by both backend and frontend

// ─── CEFR / VSTEP Levels ────────────────────────────────────────────────────

export type CEFRLevel = 'A2' | 'A2+' | 'B1-' | 'B1' | 'B1+' | 'B2-' | 'B2' | 'B2+' | 'C1-' | 'C1'
export type VSTEPSkill = 'listening' | 'reading' | 'writing' | 'speaking'
export type VSTEPPart = 'part1' | 'part2' | 'part3' | 'part4'
export type SubscriptionTier = 'free' | 'premium'

// ─── Multi-Exam Framework (FR-029) ───────────────────────────────────────────
// The learning engine (Diagnostic/IRT, drills, SRS) is exam-agnostic (CEFR/θ).
// Exam-specific structure (part labels, timing, task types, score scale) is
// data-driven from the DB so pages render dynamically without per-exam code.

/** Skill is the shared unit across exams; ExamSkill is an alias of VSTEPSkill. */
export type ExamSkill = VSTEPSkill
export type ExamType = 'vstep' | 'ielts_academic' | 'ielts_general'
export type ScoreScale = 'vstep_0_10' | 'ielts_band_0_9'

/** A single part/section/task within a skill, as configured per exam. */
export interface ExamSkillPart {
  examType: ExamType
  skill: ExamSkill
  /** e.g. 'part1' | 'section1' | 'task1' — opaque code used to tag content. */
  partCode: string
  sortOrder: number
  labelVi: string
  labelEn: string
  questionCount: number | null
  timeSeconds: number | null
  /** e.g. 'picture_description' | 'cue_card' | 'chart_description'. */
  taskType: string | null
  allowedQuestionTypes: QuestionType[]
}

/** CEFR band → display score range, per exam. Used by score mapping. */
export type ScoreMapping = Partial<Record<'A2' | 'B1' | 'B2' | 'C1', string>>

/** Summary used in the onboarding exam picker. */
export interface ExamSummary {
  examType: ExamType
  displayName: string
  scoreScale: ScoreScale
  isEnabled: boolean
  sortOrder: number
}

/** Full structure consumed by skill pages to render dynamically. */
export interface ExamFramework extends ExamSummary {
  scoreMapping: ScoreMapping
  /** Parts grouped by skill, each already sorted by sortOrder. */
  skills: Record<ExamSkill, ExamSkillPart[]>
}

/** One choice in the admin level picker, on the exam's native scale. */
export interface LevelOption {
  /** Internal CEFR bucket the engine filters on (A2–C1). */
  cefr: 'A2' | 'B1' | 'B2' | 'C1'
  /** Native-scale label shown to the admin (e.g. 'B2' for VSTEP, 'Band 5.5-6.5' for IELTS). */
  label: string
  /** Default difficulty_score range [min,max] (1–10) for items in this bucket. */
  difficulty: [number, number]
}

const CEFR_ORDER: Array<'A2' | 'B1' | 'B2' | 'C1'> = ['A2', 'B1', 'B2', 'C1']

/** Default within-bucket difficulty_score spread per CEFR level (exam-agnostic, 1–10). */
const CEFR_DIFFICULTY: Record<'A2' | 'B1' | 'B2' | 'C1', [number, number]> = {
  A2: [2, 4], B1: [4, 6], B2: [6, 8], C1: [8, 10],
}

/**
 * Build the level-picker options for an exam from its CEFR→display mapping.
 * VSTEP shows raw CEFR; IELTS shows the band range mapped to each CEFR bucket.
 * Single source of truth shared by the generator UI and the backend.
 */
export function listLevelOptions(scoreScale: ScoreScale, mapping: ScoreMapping): LevelOption[] {
  return CEFR_ORDER.map((cefr) => {
    const range = mapping[cefr]
    const label = scoreScale === 'vstep_0_10' || !range ? cefr : `Band ${range}`
    return { cefr, label, difficulty: CEFR_DIFFICULTY[cefr] }
  })
}

/** Default [min,max] difficulty_score for a CEFR bucket (tolerates 'B2+', 'B1-'). */
export function cefrDifficultyRange(cefr: string): [number, number] {
  const base = cefr.replace(/[+-]/g, '') as 'A2' | 'B1' | 'B2' | 'C1'
  return CEFR_DIFFICULTY[base] ?? [4, 6]
}

/** Narrow a bucket's difficulty range by an easy/medium/hard bias (for the generator). */
export function biasedDifficultyRange(cefr: string, bias?: 'easy' | 'medium' | 'hard'): [number, number] {
  const [lo, hi] = cefrDifficultyRange(cefr)
  const w = hi - lo
  if (bias === 'easy') return [lo, lo + w / 2]
  if (bias === 'hard') return [lo + w / 2, hi]
  if (bias === 'medium') return [lo + w / 4, hi - w / 4]
  return [lo, hi]
}

/** Evenly spread `quantity` difficulty_score values across [min,max], rounded to 0.1. */
export function spreadDifficulty(min: number, max: number, quantity: number): number[] {
  if (quantity <= 1) return [Math.round(((min + max) / 2) * 10) / 10]
  return Array.from({ length: quantity }, (_, i) =>
    Math.round((min + ((max - min) * i) / (quantity - 1)) * 10) / 10,
  )
}

// ─── User & Learning Profile ─────────────────────────────────────────────────

export interface User {
  id: string
  email: string
  name: string
  subscriptionTier: SubscriptionTier
  /** Only set for premium users — null for free (free tier never expires) */
  subscriptionExpiresAt: string | null
  createdAt: string
}

export interface LearningProfile {
  id: string
  userId: string
  examType: ExamType
  /** VSTEP: CEFRLevel; IELTS: band string like '6.5'. Widened to string. */
  targetLevel: string
  examDate: string | null
  dailyStudyMinutes: number
  currentLevelListening: number | null
  currentLevelReading: number | null
  currentLevelWriting: number | null
  currentLevelSpeaking: number | null
  interestTags: string[]
  updatedAt: string
}

// ─── Diagnostic / IRT ────────────────────────────────────────────────────────

export interface DiagnosticResult {
  overallLevel: CEFRLevel
  skillBreakdown: Record<VSTEPSkill, SkillResult>
  recommendedTarget: CEFRLevel
  recommendedDaysNeeded: number
  prioritySkills: VSTEPSkill[]
}

export interface SkillResult {
  level: CEFRLevel
  score: number
  weaknesses: string[]
}

// ─── Learning Session ─────────────────────────────────────────────────────────

export type SessionStep = 'quick_diagnostic' | 'real_content' | 'micro_drill' | 'vocab_review'

export interface LearningSession {
  id: string
  userId: string
  sessionDate: string
  startedAt: string
  completedAt: string | null
  durationSeconds: number | null
  stepsCompleted: number
  skillFocus: VSTEPSkill
  xpEarned: number
}

export interface SessionConfig {
  availableMinutes: number   // 5, 10, 15, 30+
  skillFocus: VSTEPSkill
  steps: SessionStep[]
  program?: AdaptiveProgram
}

// ─── Adaptive Program (FR-003 / FR-006) ───────────────────────────────────────
// Generated from the learner's recent error patterns each login / session start.

export interface TargetWeakness {
  skill: string
  vstepPart: string | null
  errorType: string
  errorCount: number
  weight: number
}

export interface AdaptiveProgram {
  skillFocus: VSTEPSkill
  difficultyLevel: number          // 1–7
  successRate: number              // 0–1, last sessions
  /** Top 1–2 error types the micro-drill should target. */
  targetErrorTypes: TargetWeakness[]
  /** Skills flagged as critical focus (low accuracy) for dashboard highlighting. */
  weakSkills: string[]
  skillAccuracy: Record<string, number>
  vocabDue: number
  /** Raw error_type of the #1 weakness — frontend formats the "today's focus" label. */
  focusErrorType: string | null
  steps: SessionStep[]
}

// ─── Content Library ─────────────────────────────────────────────────────────

export type ContentSource = 'ted' | 'bbc' | 'guardian' | 'voa' | 'npr' | 'vstep_internal'

export interface ContentItem {
  id: string
  source: ContentSource
  title: string
  url: string
  cefrLevel: CEFRLevel
  skill: VSTEPSkill
  vstepParts: VSTEPPart[]
  topicTags: string[]
  difficultyScore: number
  durationSeconds: number | null   // audio/video
  wordCount: number | null          // reading
  transcript: string | null
  isActive: boolean
  createdAt: string
}

export interface VSTEPContentMapping {
  skill: VSTEPSkill
  parts: VSTEPPart[]
  questionTypes: string[]
  targetLevels: CEFRLevel[]
}

// ─── Questions & Attempts ─────────────────────────────────────────────────────

export type QuestionType =
  | 'multiple_choice'
  | 'true_false_not_given'
  | 'note_completion'
  | 'gap_fill'
  | 'matching_headings'

export interface Question {
  id: string
  contentId: string
  skill: VSTEPSkill
  vstepPart: VSTEPPart
  questionType: QuestionType
  prompt: string
  options: string[] | null
  correctAnswer: string
  explanation: string
  difficultyScore: number
  errorType: string | null  // inference, vocabulary, grammar, cohesion, etc.
}

export interface QuestionAttempt {
  id: string
  userId: string
  questionId: string
  sessionId: string
  answeredAt: string
  isCorrect: boolean
  timeSpentSeconds: number
  skill: VSTEPSkill
  errorType: string | null
  vstepPart: VSTEPPart
}

// ─── Vocabulary / SRS ─────────────────────────────────────────────────────────

export interface UserVocabulary {
  id: string
  userId: string
  word: string
  contextSentence: string
  sourceContentId: string
  easinessFactor: number   // SM-2 default 2.5
  intervalDays: number
  nextReviewDate: string
  repetitions: number
  createdAt: string
  /** LME-2: short AI definition shown after reveal (null until generated). */
  definition?: string | null
  /** LME-2: 2–3 AI example sentences using the word in context. */
  examples?: string[]
  /** LME-5 (FSRS): memory stability in days; null while on SM-2 / never reviewed. */
  stability?: number | null
  /** LME-5 (FSRS): card difficulty 1–10. */
  difficulty?: number | null
  /** LME-5 (FSRS): predicted recall probability today, 0–100 (null if unknown). */
  retention?: number | null
}

/** LME-1 Active Recall: which vocab learning methods are enabled for a user. */
export interface VocabMethods {
  activeRecall: boolean
  contextExamples: boolean
  /** LME-5: FSRS scheduler active (else SM-2). */
  fsrs: boolean
}

/** LME-1 Active Recall: result of grading a typed answer. */
export interface VocabRecallResult {
  correct: boolean
  /** 'exact' | 'close' (typo/accent) | 'wrong' — drives the SM-2 grade. */
  match: 'exact' | 'close' | 'wrong'
  /** SM-2 quality (0–5) derived from the match, already applied. */
  quality: number
  word: string
  definition: string | null
  examples: string[]
}

// ─── Writing ──────────────────────────────────────────────────────────────────

export type WritingTaskType = 'task1' | 'task2'

export interface WritingSubmission {
  id: string
  userId: string
  sessionId: string
  taskType: WritingTaskType
  taskPrompt: string
  originalText: string
  wordCount: number
  submittedAt: string
  scores: WritingScores | null
  predictedLevel: CEFRLevel | null
  targetLevel: CEFRLevel
  annotations: WritingAnnotation[]
  rewriteSuggestion: string | null
  rewriteTriggered: boolean
  aiProvider: string
  aiModelId: string
  scoringLatencyMs: number | null
}

export interface WritingScores {
  overall: number
  taskAchievement: number
  coherenceCohesion: number
  lexicalResource: number
  grammaticalRange: number
}

// IELTS Academic Writing Task 1 figure (FR-029-B): a chart/table the learner
// must describe. Stored as JSON on the writing prompt and rendered client-side.
export type WritingFigureType = 'bar' | 'line' | 'pie' | 'table'

export interface WritingFigureSeries {
  name: string
  values: number[]
}

export interface WritingFigure {
  type: WritingFigureType
  title: string
  unit?: string
  /** x-axis labels (bar/line) or row labels (table). */
  categories: string[]
  series: WritingFigureSeries[]
}

export type AnnotationType = 'grammar' | 'lexical' | 'cohesion' | 'task_achievement'
export type AnnotationSeverity = 'error' | 'improvement'

export interface WritingAnnotation {
  id: string
  type: AnnotationType
  severity: AnnotationSeverity
  startChar: number
  endChar: number
  originalText: string
  suggestedText: string
  explanation: string
  vstepCriterion: string
}

// ─── Speaking ─────────────────────────────────────────────────────────────────

export type SpeakingPartType = 'part1' | 'part2' | 'part3'

export interface SpeakingSubmission {
  id: string
  userId: string
  sessionId: string
  partType: SpeakingPartType
  questionPrompt: string
  audioR2Key: string
  audioDurationSeconds: number
  submittedAt: string
  transcript: string | null
  transcriptProvider: string | null
  pronunciationScore: number | null
  fluencyScore: number | null
  prosodyScore: number | null
  phonemeDetails: PhonemeDetail[] | null
  pronunciationProvider: string | null
  scoreVocabulary: number | null
  scoreGrammar: number | null
  scoreTaskAchievement: number | null
  scoreOverall: number | null
  contentProvider: string | null
  totalLatencyMs: number | null
}

export interface PhonemeDetail {
  phoneme: string
  gopScore: number   // Goodness of Pronunciation 0-100
  isCorrect: boolean
  expectedPhoneme: string | null
}

// ─── Gamification ─────────────────────────────────────────────────────────────

export interface Streak {
  userId: string
  currentStreak: number
  longestStreak: number
  lastActivityDate: string | null
  freezeCount: number
}

export type XPLevel =
  | 'Beginner'
  | 'Elementary'
  | 'Intermediate'
  | 'Upper-Intermediate'
  | 'Advanced'
  | 'Master'

export type MilestoneKey =
  | 'first_session'
  | 'streak_7'
  | 'streak_30'
  | 'vocab_50'
  | 'vocab_100'
  | 'writing_5'
  | 'speaking_5'
  | 'level_elementary'
  | 'level_intermediate'

export interface Milestone {
  key: MilestoneKey
  earnedAt: string | null
}

export interface UserXP {
  userId: string
  totalXP: number
  level: XPLevel
  levelIndex: number
  xpIntoLevel: number
  xpForNextLevel: number
  weeklyXP: number
}

// ─── AI Provider Config (FR-018) ──────────────────────────────────────────────

export type AIModule =
  | 'writing'
  | 'speaking_transcription'
  | 'speaking_pronunciation'
  | 'speaking_content'
  | 'drill'

export type AIProvider = 'bedrock' | 'openai' | 'azure' | 'self_hosted' | 'aws_transcribe'

export interface AIProviderConfig {
  id: string
  module: AIModule
  provider: AIProvider
  modelId: string
  fallbackProvider: AIProvider | null
  fallbackModelId: string | null
  isActive: boolean
  promptCachingEnabled: boolean
  batchApiEnabled: boolean
  autoFallbackEnabled: boolean
  awsCreditThresholdSwitch: number
  updatedAt: string
  updatedBy: string
}

export interface AIRequestLog {
  id: string
  userId: string
  module: AIModule
  provider: AIProvider
  modelId: string
  inputTokens: number | null
  outputTokens: number | null
  audioSeconds: number | null
  estimatedCostUsd: number
  latencyMs: number
  isFallback: boolean
  errorCode: string | null
  createdAt: string
}

// ─── API Response shapes ──────────────────────────────────────────────────────

export interface ApiResponse<T> {
  success: boolean
  data: T
  error?: string
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}
